import { Attr, Model } from '@mode-switch/model';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { escapeAttrName } from '@mode/shared/util-js';

import { iconFromType } from './formula-editor-completer-icons';
import { ExprType } from './formula-reference-docs';
import * as FormulaReferenceDocs from './formula-reference-docs';

const exprDocs = FormulaReferenceDocs.make();

// Types
interface MatchData {
  meta: string;
  icon: string;
  type?: string;
}

const enum MetaType {
  Calculation = 'Calculation',
  Field = 'Field',
  Function = 'Function',
  Statement = 'Statement',
}

function completionComparer(a: AceAjax.Completion, b: AceAjax.Completion) {
  return a.caption > b.caption ? 1 : a.caption === b.caption ? 0 : -1;
}

export const makeAccessCompleter = (model: FlamingoTypes.ModeModel, viewName: string): AceAjax.Completer => ({
  getCompletions: (
    editor: AceAjax.Editor,
    session: AceAjax.IEditSession,
    pos: AceAjax.Position,
    prefix: string,
    callback: AceAjax.CompletionCallback
  ) => {
    // Don't attempt autocomplete if the first character is a number
    if (/^\d/.test(prefix)) {
      return;
    }

    const token = session.getTokenAt(pos.row, pos.column) as unknown as { type: string } | null;
    if (token?.type === 'string') {
      return callback(null, []);
    }

    const matches = new Map<string, MatchData>();
    const matchList: AceAjax.Completion[] = [];

    let onlyAttrs = false;
    const prefixChar = session.getLine(pos.row)[pos.column - prefix.length - 1];
    if (prefixChar === '[') {
      // This will limit the autocomplete list to Column/Calc attrs
      // It also useful for determining whether we should add square brackets around the completion
      onlyAttrs = true;
    }

    prefix = prefix.toLowerCase();
    const attrList: AceAjax.Completion[] = [];
    for (const attr of Model.Views.attrs(model, viewName)) {
      let meta: string;
      let icon: string;

      switch (attr.source) {
        case Attr.Source.Calc:
          meta = MetaType.Calculation;
          break;
        case Attr.Source.Column:
          meta = MetaType.Field;
          break;
      }

      icon = `${meta.toLowerCase()} ${iconFromType(attr.formula.type.name, attr.type, meta === MetaType.Calculation)}`;

      const attrName = escapeAttrName(attr.name);
      if (attrName === 'Invalid') {
        icon = `${meta.toLowerCase()} icon-invalid-calc`;
      }

      if (attrName.toLowerCase().startsWith(prefix)) {
        matches.set(attrName, { meta, icon });
      } else {
        attrList.push({ icon, meta, value: !onlyAttrs ? `[${attrName}]` : attrName, caption: attr.name });
      }
    }

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    const exprNames = Object.keys(exprDocs).sort(collator.compare);
    const expressions = exprNames.map((exprName) => exprDocs[exprName]);
    const funcList: AceAjax.Completion[] = [];
    const statementList: AceAjax.Completion[] = [];
    let meta: string;
    if (!onlyAttrs) {
      expressions.forEach((expression) => {
        const { type, category, name } = expression;
        meta = category;

        if (name.toLowerCase().startsWith(prefix)) {
          matches.set(name, {
            meta,
            icon: `${type.toLowerCase()} icon-function`,
            type,
          });
        } else if (type === ExprType.Function) {
          funcList.push({
            meta,
            value: `${name}(`,
            caption: name,
            icon: `${type.toLowerCase()} icon-function`,
            type,
          });
        } else if (type === ExprType.Statement) {
          statementList.push({
            meta,
            value: `${name} `,
            caption: name,
            icon: `${type.toLowerCase()} icon-function`,
            type,
          });
        }
      });
    }

    matches.forEach((data, completion) => {
      const { meta, icon, type } = data;
      let value = completion;
      switch (type) {
        case MetaType.Statement:
          value = value + ' ';
          break;
        case MetaType.Function:
          value = value + '(';
          break;
        default:
          if (!onlyAttrs) {
            value = '[' + value + ']';
          }
      }

      matchList.push({ meta, value, caption: completion, icon });
    });

    // Sort the results that need it
    attrList.sort(completionComparer);
    matchList.sort(completionComparer);

    const completions = matchList.concat(attrList).concat(funcList).concat(statementList);
    completions.forEach((comp, index) => {
      // score the completions to override Ace's default ordering
      comp.score = -index;
    });

    callback(null, completions);
  },
});

export const formatSignature = (signature: string): string => {
  switch (signature) {
    case exprDocs.CASE.sign:
      return 'CASE \nWHEN \nTHEN ELSE END';
    case exprDocs.IF.sign:
      return 'IF THEN \nELSEIF THEN \nELSE END';
    case exprDocs.AND.sign:
      return 'AND';
    case exprDocs.OR.sign:
      return 'OR';
    default:
      return signature.replace(/\(.*\)/, '()');
  }
};
